import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { UserKyc } from "@domain/userKyc/userKyc";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { mobile } from "@ui/styles/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { Description } from "@ui/components/containers/description";
import { RadioButton } from "@ui/components/inputs/radio";
import { Row, Stack, stack } from "@ui/components/layout";
import { LinkExternal } from "@ui/components/linkExternal";
import { UserKycEditionModal } from "@ui/components/userKyc/userKycEditionModal";
import { MONETIZATION_RULE_INFOS } from "./monetizationInfos";

const url_rule = "https://www.onemillionafricantalents.com/wp-content/uploads/2022/08/REGLEMENT_CHALLENGE_ENG-1.pdf";
interface OmatModalInoProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	redirectTo?: () => void;
	onMonetizationTermsChange: (openKycEditionModal: (onKycChange: (userKyc: UserKyc) => void) => void) => void;
	onKycChange?: (userKyc: UserKyc) => void;
}

export const MonetizationRuleModal: React.FC<OmatModalInoProps> = ({
	close,
	redirectTo,
	onMonetizationTermsChange,
	onKycChange,
	...htmlProps
}) => {
	const { createModal } = useModalTools();

	const [rules, setRules] = useState<{ challengeRule: boolean; conditionRule: boolean }>({
		challengeRule: false,
		conditionRule: false,
	});

	const openKycEditionModal = (): void => {
		createModal((close) => <UserKycEditionModal onKycChange={(userKyc) => onKycChange?.(userKyc)} close={close} />);
	};

	const currentProfile = useCurrentProfile();
	const { currentProfileService } = useServices();

	const { run, loading } = useAsync(async (): Promise<CurrentProfile> => {
		const profile = await currentProfileService.saveProfileInfos({
			...currentProfile,
			monetizationTermsConsented: true,
		});
		return profile;
	}, [currentProfile]);
	return (
		<Container close={close} title={I18n.t("shareroom.terms.title")} {...htmlProps}>
			<Stack gap={40}>
				<StyledDescription>{I18n.t("shareroom.terms.intro")}</StyledDescription>
				<List>
					{MONETIZATION_RULE_INFOS.map((rule) => (
						<li key={rule}>{I18n.t(`shareroom.terms.${rule.toLocaleLowerCase()}`)}</li>
					))}
				</List>
				<StyledDescription>{I18n.t("shareroom.terms.notice")}</StyledDescription>
				<Stack gap={10}>
					<Row gap={12}>
						<RadioButton
							checkbox
							id="challenge"
							name="challenge_button"
							checked={rules.challengeRule}
							onChange={() => setRules((rules) => ({ ...rules, challengeRule: !rules.challengeRule }))}
						/>
						<Row>
							{/* {I18n.t("program.poppin.rules.label1")} */}
							<LinkExternal to={url_rule}>
								<StyledRuleChalengeText>
									<b>{I18n.t("accept")}</b>
								</StyledRuleChalengeText>
							</LinkExternal>
						</Row>
					</Row>
					<RadioButton
						checkbox
						label={I18n.t("shareroom.terms.accept")}
						id="condition"
						name="condition_button"
						checked={rules.conditionRule}
						onChange={() => setRules((rules) => ({ ...rules, conditionRule: !rules.conditionRule }))}
					/>
				</Stack>
			</Stack>
			{loading ? (
				<ClipLoader />
			) : (
				<ActionBloc>
					<PrimaryButton
						onClick={async () => {
							try {
								await run();
								close();
								onMonetizationTermsChange(openKycEditionModal);
							} catch (error) {
								console.log("error", error);
							}
						}}
						disabled={!rules.challengeRule}
					>
						{I18n.t("accept")}
					</PrimaryButton>
				</ActionBloc>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	position: relative;
	.modal-title {
		margin-bottom: 40px;
		@media ${mobile} {
			width: 100%;
		}
	}
`;

const StyledDescription = styled(Description)`
	font-size: 0.938rem;
`;

const List = styled.ul`
	${stack(20, "center")};
	margin-left: 30px;
	> li {
		${({ theme }) => theme.textStyles.description};
		font-size: 0.938rem;
		@media ${mobile} {
			font-size: 0.938rem;
		}
	}
`;

const ActionBloc = styled.div`
	margin-top: 40px;
	display: flex;
	width: 100%;
	gap: 10px;
	flex-direction: column;
	align-items: center;
`;

const StyledRuleChalengeText = styled.div`
	text-decoration: underline;
	cursor: pointer;
	margin-left: 4px;
`;
